import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CloudNavBar } from "../components/CloudNavBar";
import { Combobox } from "@/components/ui/combobox";

interface Usage {
  plan: { id: string; name: string; sso_enabled: boolean };
  usage: { checks: number; alert_channels: number; users: number };
  limits: { max_checks: number; max_alert_channels: number; max_users: number };
}

interface SSOConfig {
  tenant_id: string;
  issuer_url: string;
  client_id: string;
  client_secret: string;
  redirect_url: string;
}

function UsageMeter({ label, current, max }: { label: string; current: number; max: number }) {
  const unlimited = max < 0;
  const pct = unlimited ? 0 : Math.min((current / max) * 100, 100);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{current} / {unlimited ? "Unlimited" : max}</span>
      </div>
      {!unlimited && (
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-yellow-500" : "bg-blue-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function SSOConfigSection() {
  const [config, setConfig] = useState<SSOConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({
    issuer_url: "",
    client_id: "",
    client_secret: "",
    redirect_url: "",
  });

  const ssoLoginURL = `${window.location.origin}/api/auth/sso/${getTenantSlug()}`;

  useEffect(() => {
    api.get<SSOConfig>("/tenants/current/sso")
      .then((cfg) => {
        setConfig(cfg);
        setForm({
          issuer_url: cfg.issuer_url,
          client_id: cfg.client_id,
          client_secret: "", // masked, don't prefill
          redirect_url: cfg.redirect_url || "",
        });
      })
      .catch(() => {
        // No SSO config yet
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const payload = { ...form };
      // If secret is empty and config exists, user didn't change it - send placeholder
      if (!payload.client_secret && config) {
        payload.client_secret = "__unchanged__";
      }
      const saved = await api.put<SSOConfig>("/tenants/current/sso", payload);
      setConfig(saved);
      setSuccess("SSO configuration saved successfully.");
      setForm((f) => ({ ...f, client_secret: "" }));
    } catch (e: any) {
      setError(e?.message || "Failed to save SSO config");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Remove SSO configuration? Users will no longer be able to log in via SSO.")) return;
    setError("");
    setSuccess("");
    try {
      await api.del("/tenants/current/sso");
      setConfig(null);
      setForm({ issuer_url: "", client_id: "", client_secret: "", redirect_url: "" });
      setSuccess("SSO configuration removed.");
    } catch (e: any) {
      setError(e?.message || "Failed to delete SSO config");
    }
  };

  if (loading) return <p className="text-muted-foreground text-sm">Loading SSO config...</p>;

  return (
    <div className="bg-card rounded-lg shadow p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">SSO / OIDC Configuration</h2>
        {config && (
          <span className="text-xs bg-green-100 text-green-800 rounded-full px-2 py-0.5">Active</span>
        )}
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>}
      {success && <p className="text-sm text-green-600 bg-green-50 rounded p-2">{success}</p>}

      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium mb-1">Issuer URL</span>
          <input
            type="url"
            className="w-full border rounded px-3 py-2 text-sm bg-background"
            placeholder="https://accounts.google.com"
            value={form.issuer_url}
            onChange={(e) => setForm((f) => ({ ...f, issuer_url: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium mb-1">Client ID</span>
          <input
            type="text"
            className="w-full border rounded px-3 py-2 text-sm bg-background"
            placeholder="your-client-id"
            value={form.client_id}
            onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium mb-1">
            Client Secret {config && <span className="text-muted-foreground">(leave blank to keep current)</span>}
          </span>
          <input
            type="password"
            className="w-full border rounded px-3 py-2 text-sm bg-background"
            placeholder={config ? "********" : "your-client-secret"}
            value={form.client_secret}
            onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="block text-sm font-medium mb-1">
            Redirect URL <span className="text-muted-foreground">(optional, auto-detected if empty)</span>
          </span>
          <input
            type="url"
            className="w-full border rounded px-3 py-2 text-sm bg-background"
            placeholder={`${window.location.origin}/api/auth/sso/callback`}
            value={form.redirect_url}
            onChange={(e) => setForm((f) => ({ ...f, redirect_url: e.target.value }))}
          />
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving || !form.issuer_url || !form.client_id || (!form.client_secret && !config)}
          className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save SSO Config"}
        </button>
        {config && (
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:opacity-90"
          >
            Remove SSO
          </button>
        )}
      </div>

      {config && (
        <div className="border-t pt-4 mt-4">
          <p className="text-sm text-muted-foreground mb-1">SSO Login URL (share with your team):</p>
          <code className="block bg-muted rounded px-3 py-2 text-sm break-all select-all">{ssoLoginURL}</code>
        </div>
      )}
    </div>
  );
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  plan_id: string;
  status: string;
  timezone: string;
  created_at: string;
  updated_at: string;
}

const TIMEZONE_FALLBACK = ['UTC', 'Europe/London', 'Europe/Kyiv', 'America/New_York', 'America/Los_Angeles', 'Asia/Tokyo'];

const intlWithSupportedValues = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
const ALL_TIMEZONES: string[] = typeof intlWithSupportedValues.supportedValuesOf === 'function'
  ? intlWithSupportedValues.supportedValuesOf('timeZone')
  : TIMEZONE_FALLBACK;

function formatTimezoneLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    const offset = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    return `${tz} (${offset})`;
  } catch {
    return tz;
  }
}

function TimezoneSection() {
  const [timezone, setTimezone] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    api.get<Tenant>('/tenants/current')
      .then((t) => setTimezone(t.timezone || 'UTC'))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const updated = await api.patch<Tenant>('/tenants/current', { timezone });
      setTimezone(updated.timezone || timezone);
      setSuccess('Timezone saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save timezone.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted-foreground text-sm">Loading timezone...</p>;

  return (
    <div className="bg-card rounded-lg shadow p-6 space-y-4">
      <h2 className="text-lg font-semibold">Timezone</h2>
      <p className="text-sm text-muted-foreground">
        Alert timestamps and scheduled reports will use this timezone.
      </p>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded p-2">{error}</p>}
      {success && <p className="text-sm text-green-600 bg-green-50 rounded p-2">{success}</p>}

      <label className="space-y-2 block">
        <span className="block text-sm font-medium">Timezone</span>
        <Combobox
          value={timezone}
          onChange={(val) => { setTimezone(val); setSuccess(''); setError(''); }}
          options={ALL_TIMEZONES}
          placeholder="Search timezone…"
          emptyMessage="No timezone found."
        />
        {timezone && (
          <p className="text-xs text-muted-foreground">{formatTimezoneLabel(timezone)}</p>
        )}
      </label>

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Timezone'}
        </button>
      </div>
    </div>
  );
}

function getTenantSlug(): string {
  // Extract slug from the current tenant data cached in localStorage or URL
  try {
    const raw = localStorage.getItem("tenant");
    if (raw) {
      const t = JSON.parse(raw);
      if (t.slug) return t.slug;
    }
  } catch {
    // ignore
  }
  return "your-tenant-slug";
}

export default function AccountPage() {
  const [data, setData] = useState<Usage | null>(null);

  useEffect(() => {
    api.get<Usage>("/tenants/current/usage").then(setData).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <div className="max-w-3xl mx-auto py-8 px-6">
      <h1 className="text-2xl font-bold mb-6">Account</h1>
      {!data ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg shadow p-6 space-y-6">
            <div>
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <p className="text-xl font-semibold">{data.plan.name}</p>
            </div>
            <div className="space-y-4">
              <UsageMeter label="Checks" current={data.usage.checks} max={data.limits.max_checks} />
              <UsageMeter label="Alert Channels" current={data.usage.alert_channels} max={data.limits.max_alert_channels} />
              <UsageMeter label="Users" current={data.usage.users} max={data.limits.max_users} />
            </div>
            <div className="flex gap-3 pt-2 flex-wrap">
              <Link to="/account/billing" className="text-sm text-primary hover:underline">Manage Billing</Link>
              <Link to="/account/team" className="text-sm text-primary hover:underline">Manage Team</Link>
              <Link to="/account/api-keys" className="text-sm text-primary hover:underline">API Keys</Link>
            </div>
          </div>

          {data.plan.sso_enabled ? (
            <SSOConfigSection />
          ) : (
            <div className="bg-card rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-2">SSO / OIDC</h2>
              <p className="text-sm text-muted-foreground">
                Single Sign-On is available on the Enterprise plan.{" "}
                <Link to="/account/billing" className="text-primary hover:underline">Upgrade</Link> to configure OIDC-based SSO for your team.
              </p>
            </div>
          )}

          <TimezoneSection />
        </div>
      )}
      </div>
    </div>
  );
}
